import cashJournalService from '@/services/cashJournalService';

const state = {
    journals: [],
    currentJournal: null,
    journalStatus: null,
    transactions: [],
    currentTransaction: null,
    reports: [],
    currentReport: null,
    loading: {
        journals: false,
        journal: false,
        status: false,
        transactions: false,
        transaction: false,
        reports: false,
        report: false,
    },
    error: null,
    transactionPagination: {
        cursor: null,
        limit: 50,
        hasMore: false,
    },
};

const mutations = {
    SET_JOURNALS(state, journals) {
        state.journals = journals;
    },
    SET_CURRENT_JOURNAL(state, journal) {
        state.currentJournal = journal;
    },
    ADD_JOURNAL(state, journal) {
        state.journals.push(journal);
    },
    UPDATE_JOURNAL(state, updatedJournal) {
        const index = state.journals.findIndex((j) => j.uuid === updatedJournal.uuid);
        if (index !== -1) {
            state.journals.splice(index, 1, updatedJournal);
        }
        if (state.currentJournal?.uuid === updatedJournal.uuid) {
            state.currentJournal = updatedJournal;
        }
    },
    REMOVE_JOURNAL(state, journalUuid) {
        state.journals = state.journals.filter((j) => j.uuid !== journalUuid);
        if (state.currentJournal?.uuid === journalUuid) {
            state.currentJournal = null;
        }
    },
    SET_JOURNAL_STATUS(state, status) {
        state.journalStatus = status;
    },
    SET_TRANSACTIONS(state, transactions) {
        state.transactions = transactions;
    },
    APPEND_TRANSACTIONS(state, transactions) {
        state.transactions.push(...transactions);
    },
    SET_CURRENT_TRANSACTION(state, transaction) {
        state.currentTransaction = transaction;
    },
    ADD_TRANSACTION(state, transaction) {
        state.transactions.unshift(transaction);
    },
    UPDATE_TRANSACTION(state, updatedTransaction) {
        const index = state.transactions.findIndex((t) => t.uuid === updatedTransaction.uuid);
        if (index !== -1) {
            state.transactions.splice(index, 1, updatedTransaction);
        }
        if (state.currentTransaction?.uuid === updatedTransaction.uuid) {
            state.currentTransaction = updatedTransaction;
        }
        if (state.currentJournal?.journalTransactions) {
            const journalIndex = state.currentJournal.journalTransactions.findIndex(
                (t) => t.uuid === updatedTransaction.uuid,
            );
            if (journalIndex !== -1) {
                state.currentJournal.journalTransactions.splice(
                    journalIndex,
                    1,
                    updatedTransaction,
                );
            }
        }
    },
    REMOVE_TRANSACTION(state, transactionUuid) {
        state.transactions = state.transactions.filter((t) => t.uuid !== transactionUuid);
        if (state.currentTransaction?.uuid === transactionUuid) {
            state.currentTransaction = null;
        }
        if (state.currentJournal?.journalTransactions) {
            state.currentJournal.journalTransactions =
                state.currentJournal.journalTransactions.filter((t) => t.uuid !== transactionUuid);
        }
    },
    SET_REPORTS(state, reports) {
        state.reports = reports;
    },
    SET_CURRENT_REPORT(state, report) {
        state.currentReport = report;
    },
    ADD_REPORT(state, report) {
        state.reports.push(report);
    },
    UPDATE_REPORT(state, updatedReport) {
        const index = state.reports.findIndex((r) => r.uuid === updatedReport.uuid);
        if (index !== -1) {
            state.reports.splice(index, 1, updatedReport);
        }
        if (state.currentReport?.uuid === updatedReport.uuid) {
            state.currentReport = updatedReport;
        }
        if (state.currentJournal?.dailyJournalReports) {
            const journalIndex = state.currentJournal.dailyJournalReports.findIndex(
                (r) => r.uuid === updatedReport.uuid,
            );
            if (journalIndex !== -1) {
                state.currentJournal.dailyJournalReports.splice(journalIndex, 1, updatedReport);
            }
        }
    },
    SET_LOADING(state, { key, value }) {
        state.loading[key] = value;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_TRANSACTION_PAGINATION(state, pagination) {
        state.transactionPagination = { ...state.transactionPagination, ...pagination };
    },
    RESET_TRANSACTIONS(state) {
        state.transactions = [];
        state.transactionPagination = {
            cursor: null,
            limit: 50,
            hasMore: false,
        };
    },
    CLEAR_CURRENT_JOURNAL(state) {
        state.currentJournal = null;
        state.journalStatus = null;
    },
    CLEAR_CURRENT_TRANSACTION(state) {
        state.currentTransaction = null;
    },
    CLEAR_CURRENT_REPORT(state) {
        state.currentReport = null;
    },
};

const actions = {
    async fetchJournals({ commit }, params = {}) {
        commit('SET_LOADING', { key: 'journals', value: true });
        commit('SET_ERROR', null);
        try {
            const journals = await cashJournalService.getJournals(params);
            commit('SET_JOURNALS', journals || []);
            return journals;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to fetch cash journals:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'journals', value: false });
        }
    },

    async fetchDriverJournals({ dispatch }, { driverNumber, isClosed }) {
        const params = { driverNumber };
        if (isClosed !== undefined) {
            params.isClosed = isClosed;
        }
        return await dispatch('fetchJournals', params);
    },

    async fetchJournal({ commit }, uuid) {
        commit('SET_LOADING', { key: 'journal', value: true });
        commit('SET_ERROR', null);
        try {
            const journal = await cashJournalService.getJournal(uuid);
            commit('SET_CURRENT_JOURNAL', journal);
            return journal;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to fetch cash journal:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'journal', value: false });
        }
    },

    async fetchJournalStatus({ commit }, uuid) {
        commit('SET_LOADING', { key: 'status', value: true });
        commit('SET_ERROR', null);
        try {
            const status = await cashJournalService.getJournalStatus(uuid);
            commit('SET_JOURNAL_STATUS', status);
            return status;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to fetch journal status:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'status', value: false });
        }
    },

    async fetchTransactions({ commit, state }, { params = {}, append = false }) {
        commit('SET_LOADING', { key: 'transactions', value: true });
        commit('SET_ERROR', null);
        try {
            const queryParams = {
                ...params,
                limit: params.limit || state.transactionPagination.limit,
                cursor: append ? state.transactionPagination.cursor : params.cursor,
            };

            const transactions = await cashJournalService.getTransactions(queryParams);

            if (append) {
                commit('APPEND_TRANSACTIONS', transactions || []);
            } else {
                commit('SET_TRANSACTIONS', transactions || []);
            }

            if (transactions && transactions.length > 0) {
                const lastTransaction = transactions[transactions.length - 1];
                commit('SET_TRANSACTION_PAGINATION', {
                    cursor: lastTransaction.uuid,
                    hasMore: transactions.length === queryParams.limit,
                });
            } else {
                commit('SET_TRANSACTION_PAGINATION', {
                    cursor: null,
                    hasMore: false,
                });
            }

            return transactions;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to fetch cash journal transactions:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'transactions', value: false });
        }
    },

    async fetchTransaction({ commit }, uuid) {
        commit('SET_LOADING', { key: 'transaction', value: true });
        commit('SET_ERROR', null);
        try {
            const transaction = await cashJournalService.getTransaction(uuid);
            commit('SET_CURRENT_TRANSACTION', transaction);
            return transaction;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to fetch cash journal transaction:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'transaction', value: false });
        }
    },

    async fetchReports({ commit }, params = {}) {
        commit('SET_LOADING', { key: 'reports', value: true });
        commit('SET_ERROR', null);
        try {
            const reports = await cashJournalService.getReports(params);
            commit('SET_REPORTS', reports || []);
            return reports;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to fetch cash journal reports:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'reports', value: false });
        }
    },

    async fetchReport({ commit }, uuid) {
        commit('SET_LOADING', { key: 'report', value: true });
        commit('SET_ERROR', null);
        try {
            const report = await cashJournalService.getReport(uuid);
            commit('SET_CURRENT_REPORT', report);
            return report;
        } catch (error) {
            commit('SET_ERROR', error);
            console.error('Failed to fetch cash journal report:', error);
            throw error;
        } finally {
            commit('SET_LOADING', { key: 'report', value: false });
        }
    },

    clearCurrentJournal({ commit }) {
        commit('CLEAR_CURRENT_JOURNAL');
    },

    clearCurrentTransaction({ commit }) {
        commit('CLEAR_CURRENT_TRANSACTION');
    },

    clearCurrentReport({ commit }) {
        commit('CLEAR_CURRENT_REPORT');
    },

    resetTransactions({ commit }) {
        commit('RESET_TRANSACTIONS');
    },
};

const getters = {
    currentJournal: (state) => state.currentJournal,
    getJournalByUuid: (state) => (uuid) => state.journals.find((j) => j.uuid === uuid),
    journalsByDriver: (state) => (driverNumber) =>
        state.journals.filter((j) => j.driver?.driverNumber === driverNumber),
    activeJournalByDriver: (state) => (driverNumber) =>
        state.journals.find((j) => j.driver?.driverNumber === driverNumber && !j.isClosed),
    closedJournalsByDriver: (state) => (driverNumber) =>
        state.journals.filter((j) => j.driver?.driverNumber === driverNumber && j.isClosed),
    getTransactionByUuid: (state) => (uuid) =>
        state.transactions.find((t) => t.uuid === uuid) ||
        state.currentJournal?.journalTransactions?.find((t) => t.uuid === uuid),
    transactionsByType: (state) => (type) => state.transactions.filter((t) => t.type === type),
    getReportByUuid: (state) => (uuid) =>
        state.reports.find((r) => r.uuid === uuid) ||
        state.currentJournal?.dailyJournalReports?.find((r) => r.uuid === uuid),
    reportsByDriver: (state) => (driverNumber) =>
        state.reports.filter((r) => r.driver?.driverNumber === driverNumber),
    lockedReports: (state) => state.reports.filter((r) => r.isLocked),
    isLoading: (state) => (key) => state.loading[key] || false,
    isLoadingAny: (state) => Object.values(state.loading).some((loading) => loading),
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
